import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge, Button, Input } from '../../../components/ui';
import { Wallet, RefreshCw, DollarSign, Search } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import { getSaldoTone, getSaldoTextColor, getSaldoBgColor } from '../../../utils/saldoColor';

// Tipos
interface MiSaldo {
  local_id: string;
  local_nombre: string;
  saldo_actual: string | null;
  es_propio?: boolean;
  credito?: {
    saldo_real?: string;
    saldo_post_sorteo?: string;
  } | null;
}

interface Movimiento {
  id: string;
  local_id: string;
  local_nombre: string;
  tipo: string;
  monto: string;
  referencia: string;
  fecha: string;
  saldo_resultante: string;
  detalle: {
    vendido: string;
    premios: string;
    comision: string;
  } | null;
  created_by: string;
  created_at: string;
}

type MovimientosResponse =
  | Movimiento[]
  | {
      items?: Movimiento[];
    };

interface MovimientosPaginados {
  items: Movimiento[];
  total_count: number;
  pagina: number;
  por_pagina: number;
}

const TIPO_LABELS: Record<string, string> = {
  LIQUIDACION_DIA: 'Liquidación',
  CIERRE_CONSOLIDADO: 'Cierre',
  ABONO: 'Abono',
  PAGO: 'Pago',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  LIQUIDACION_DIA: 'primary',
  CIERRE_CONSOLIDADO: 'neutral',
  ABONO: 'success',
  PAGO: 'warning',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

export const LocalSaldoPage: React.FC = () => {
  const [saldo, setSaldo] = useState<MiSaldo | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [saldoData, movsData] = await Promise.all([
        fetchAPI<MiSaldo>('/saldos/mi-saldo'),
        fetchAPI<MovimientosResponse>('/saldos/movimientos?limit=100'),
      ]);

      const movimientosNormalizados = Array.isArray(movsData)
        ? movsData
        : Array.isArray(movsData?.items)
          ? movsData.items
          : [];

      setSaldo(saldoData);
      setMovimientos(movimientosNormalizados.filter(m => m.tipo === 'ABONO' || m.tipo === 'PAGO'));
    } catch (e: any) {
      setError(e.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Propio: saldo_real (saldo_actual), No propio: saldo_post_sorteo
  const esPropio = saldo?.es_propio ?? false;
  const saldoMostrar = esPropio
    ? (saldo?.saldo_actual ?? '0')
    : (saldo?.credito?.saldo_post_sorteo ?? saldo?.saldo_actual ?? '0');
  const saldoNum = Number(saldoMostrar);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Saldo</h1>
          <p className="text-gray-500">{saldo?.local_nombre || 'Estado financiero con la banca'}</p>
        </div>
        <button
          onClick={cargarDatos}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          title="Refrescar"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Saldo actual */}
      {!loading && saldo && (
        <Card>
          <div className="flex items-center justify-center py-6">
            <div className="text-center">
              {(() => {
                const tone = getSaldoTone(saldoNum);
                const bgColor = getSaldoBgColor(tone);
                const textColor = getSaldoTextColor(tone);
                const iconColor = tone === 'positive' ? 'text-green-500' : tone === 'negative' ? 'text-red-500' : 'text-gray-500';
                return (
                  <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl border-2 ${bgColor}`}>
                    <Wallet size={32} className={iconColor} />
                    <div>
                      <p className={`text-4xl font-bold ${textColor}`}>
                        {formatMonto(saldoMostrar)}
                      </p>
                      <p className={`text-sm mt-1 ${tone === 'positive' ? 'text-green-500' : tone === 'negative' ? 'text-red-500' : 'text-gray-500'}`}>
                        {saldoNum > 0 ? 'Debes a la banca' :
                         saldoNum < 0 ? 'La banca te debe' :
                         'Saldo en cero'}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{esPropio ? 'Saldo real' : 'Saldo post sorteo'}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8 text-gray-500">Cargando...</div>
      )}

      {/* Historial reciente (abonos/pagos del momento) */}
      {!loading && (
        <Card padding="none">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-medium text-gray-700">Movimientos Recientes</h3>
          </div>
          {movimientos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Sin movimientos registrados</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {movimientos.map(m => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">{m.fecha}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                          {TIPO_LABELS[m.tipo] || m.tipo}
                        </Badge>
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Historial de abonos y pagos con rango de fechas */}
      <HistorialAbonosSection />
    </div>
  );
};

// ── Sección: Historial de abonos y pagos con rango de fechas ──
const HistorialAbonosSection: React.FC = () => {
  const hoy = fechaLocalYMD();
  const hace30 = (() => {
    const d = new Date(hoy + 'T12:00:00');
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();

  const [fechaDesde, setFechaDesde] = useState(hace30);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [items, setItems] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const porPagina = 50;

  const rangoError = (() => {
    if (!fechaDesde || !fechaHasta) return null;
    if (fechaDesde > fechaHasta) return 'Fecha inicio no puede ser mayor a fecha fin';
    if (fechaHasta > hoy) return 'No se permiten fechas futuras';
    return null;
  })();

  const cargar = async (pag: number = 1) => {
    if (rangoError) return;
    setLoading(true);
    setError('');
    try {
      let url = `/saldos/movimientos?pagina=${pag}&por_pagina=${porPagina}&tipos=ABONO,PAGO`;
      if (fechaDesde) url += `&fecha_desde=${fechaDesde}`;
      if (fechaHasta) url += `&fecha_hasta=${fechaHasta}`;
      const data = await fetchAPI<MovimientosPaginados>(url);
      setItems(data.items);
      setTotalCount(data.total_count);
      setPagina(data.pagina);
    } catch (e: any) {
      setError(e.message || 'Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const buscar = () => {
    setPagina(1);
    cargar(1);
  };

  const irPagina = (pag: number) => {
    cargar(pag);
  };

  const totalPaginas = Math.max(1, Math.ceil(totalCount / porPagina));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-gray-900">Historial de Abonos y Pagos</h2>
        <p className="text-sm text-gray-500">Consulta abonos y pagos en un rango de fechas</p>
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <Input
              type="date"
              value={fechaDesde}
              max={fechaHasta || hoy}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <Input
              type="date"
              value={fechaHasta}
              min={fechaDesde}
              max={hoy}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
          <Button variant="primary" onClick={buscar} disabled={loading || !!rangoError} leftIcon={<Search size={16} />}>
            {loading ? 'Cargando...' : 'Consultar'}
          </Button>
        </div>
        {rangoError && (
          <p className="mt-2 text-sm text-red-600">{rangoError}</p>
        )}
      </Card>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {items.length > 0 && (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map(m => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{m.fecha}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                        {TIPO_LABELS[m.tipo] || m.tipo}
                      </Badge>
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${Number(m.saldo_resultante) > 0 ? 'text-red-600' : Number(m.saldo_resultante) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                      {formatMonto(m.saldo_resultante)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!loading && items.length === 0 && totalCount === 0 && fechaDesde && fechaHasta && !error && (
        <Card>
          <div className="p-6 text-center text-gray-500">
            Presiona "Consultar" para buscar abonos y pagos en el rango seleccionado
          </div>
        </Card>
      )}

      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-sm text-gray-500">
            {totalCount} registro{totalCount !== 1 ? 's' : ''} — Página {pagina} de {totalPaginas}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina <= 1 || loading}
              onClick={() => irPagina(pagina - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina >= totalPaginas || loading}
              onClick={() => irPagina(pagina + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalSaldoPage;
